if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        // When true, updateOptionValues will NOT attempt to restore the
        // client's previous selection after replacing the variant-selects
        // HTML. This is used during initial hydration when the server may
        // choose a different default variant.
        this._suppressVariantSelectorRestore = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
        this.showDiscountedPriceFromBodyTag();

        // Ensure first paint respects the server-selected variant without touching the URL
        try {
          const initialVariant = this.getSelectedVariant(this);
          const initialVariantId =
            initialVariant?.id ||
            (this.dataset.initialVariantId
              ? parseInt(this.dataset.initialVariantId, 10)
              : null);
          // If no server-provided initial variant was found, but the URL
          // contains a ?variant= param, prefer that variant id (even if
          // it's out-of-stock). This ensures the page reflects the URL
          // requested variant on first paint.
          let resolvedInitialVariantId = initialVariantId;
          if (!resolvedInitialVariantId) {
            try {
              const qp = new URLSearchParams(window.location.search);
              const v = qp.get("variant");
              if (v) resolvedInitialVariantId = parseInt(v, 10);
            } catch (e) {
              /* ignore malformed URL */
            }
          }
          if (resolvedInitialVariantId) {
            // Keep hidden form inputs in sync in case of dynamic hydration
            this.updateVariantInputs(resolvedInitialVariantId);

            // Toggle any metafield-driven tabs immediately
            this.updateCollapsibleTabs(initialVariantId);

            // Update pickup availability widget if full variant data is available
            if (initialVariant) this.pickupAvailability?.update(initialVariant);

            // Fetch the server-rendered section fragment for this variant so
            // media (gallery) and price blocks are updated to match the
            // server-selected variant. We reuse the existing renderProductInfo
            // + handleUpdateProductInfo flow to keep update logic centralized.
            try {
              const requestUrl = `${this.dataset.url}?variant=${resolvedInitialVariantId}&section_id=${this.sectionId}`;
              // Prevent the client from re-applying its previous option
              // selection after we fetch the server-rendered fragment for
              // the initial variant. We want the server-selected default to
              // win on first paint.
              this._suppressVariantSelectorRestore = true;
              this.renderProductInfo({
                requestUrl,
                callback: this.handleUpdateProductInfo(this.dataset.url),
              });
            } catch (e) {
              /* non-fatal; leave initial render as-is */
            }
          }
        } catch (e) {
          // Non-fatal: proceed with existing server-rendered state
          console.warn("product-info: initial variant sync skipped", e);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        // Optimistic URL sync: if the variant data is embedded in the DOM as
        // <script type="application/json" data-variants> parse it and attempt
        // to find a matching variant so we can call updateURL immediately.
        try {
          const vsRoot = target.closest && target.closest("variant-selects");
          const script = (vsRoot || target).querySelector
            ? (vsRoot || target).querySelector(
                'script[type="application/json"][data-variants]',
              )
            : null;
          if (script && script.textContent) {
            const variants = JSON.parse(script.textContent);
            // determine selected option values if not provided
            let values = selectedOptionValues || [];
            if (!values || values.length === 0) {
              const selects = (vsRoot || target).querySelectorAll
                ? (vsRoot || target).querySelectorAll("select")
                : [];
              values = Array.from(selects)
                .map((s) => s.value)
                .filter(Boolean);
            }
            if (Array.isArray(variants) && values.length) {
              const found = variants.find((v) => {
                const opts = [v.option1, v.option2, v.option3].map((o) =>
                  o == null ? null : String(o),
                );
                for (let i = 0; i < values.length; i++) {
                  if (String(values[i]) !== String(opts[i])) return false;
                }
                return true;
              });
              if (found && typeof this.updateURL === "function") {
                this.updateURL(productUrl, found.id);
              }
            }
          }
        } catch (e) {
          // noop - optimistic update is best-effort
        }

        // Try to resolve a variant id from the selected option values and
        // update the browser URL immediately to include ?variant=ID even
        // when that variant is out-of-stock. Note: selectedOptionValues
        // are option-value ids; we prefer to use the visible labels from
        // the DOM so they match v.option1/2/3 in window.productVariants.
        try {
          const pv = this.getAllVariants();

          // Prefer reading labels from the DOM-selected inputs
          let selectedLabels = [];
          try {
            const vs = this.variantSelectors;
            if (vs) {
              const els = vs.querySelectorAll(
                "select option[selected], fieldset input:checked",
              );
              selectedLabels = Array.from(els).map((el) =>
                (el.value || el.textContent || "").toString(),
              );
            }
          } catch (e) {
            selectedLabels = [];
          }

          // Fallback: map selectedOptionValues (ids) back to DOM elements
          if (
            (!selectedLabels || !selectedLabels.length) &&
            selectedOptionValues &&
            selectedOptionValues.length &&
            this.variantSelectors
          ) {
            selectedLabels = selectedOptionValues.map((id) => {
              const el = this.variantSelectors.querySelector(
                `[data-option-value-id="${id}"]`,
              );
              return el ? el.value || el.textContent || "" : "";
            });
          }

          // Prefer any explicit data-variant-id on the event target or the
          // currently selected option/input. This is the most reliable way to
          // reflect the exact variant the user clicked (including OOS variants).
          try {
            let foundVid = null;

            // 1) If the event target itself includes a data-variant-id, use it.
            try {
              const t = event && event.target;
              if (t) {
                const attr =
                  t.getAttribute && t.getAttribute("data-variant-id");
                if (attr) foundVid = parseInt(attr, 10) || foundVid;
              }
            } catch (e) {
              /* ignore */
            }

            // 2) If a select element is present, use the selected option's dataset
            // (safer than querying for option[selected] which looks for an attribute).
            if (!foundVid && this.variantSelectors) {
              try {
                const selects = Array.from(
                  this.variantSelectors.querySelectorAll("select"),
                );
                for (const sel of selects) {
                  const opt = sel.options[sel.selectedIndex];
                  if (opt) {
                    const a = opt.getAttribute("data-variant-id");
                    if (a) {
                      foundVid = parseInt(a, 10);
                      break;
                    }
                  }
                }
              } catch (e) {
                /* ignore */
              }
            }

            // 3) For radio inputs (fieldset), look for checked input with data-variant-id
            if (!foundVid && this.variantSelectors) {
              try {
                const checked = this.variantSelectors.querySelector(
                  "fieldset input:checked[data-variant-id], input[type=radio]:checked[data-variant-id]",
                );
                if (checked) {
                  const a = checked.getAttribute("data-variant-id");
                  if (a) foundVid = parseInt(a, 10);
                }
              } catch (e) {
                /* ignore */
              }
            }

            // 4) If we found an explicit vid, update URL and skip label-matching.
            if (foundVid) {
              this.updateURL(productUrl, foundVid);
            } else if (pv && selectedLabels && selectedLabels.length) {
              // Fallback: label-matching against parsed variants JSON
              const match = pv.find(
                (v) =>
                  (!selectedLabels[0] ||
                    (v.option1 || "") === selectedLabels[0]) &&
                  (!selectedLabels[1] ||
                    (v.option2 || "") === selectedLabels[1]) &&
                  (!selectedLabels[2] ||
                    (v.option3 || "") === selectedLabels[2]),
              );
              if (match?.id) {
                this.updateURL(productUrl, match.id);
              }
            }
          } catch (e) {
            /* best-effort: ignore URL-sync failures */
          }
        } catch (e) {
          /* best-effort: ignore URL-sync failures */
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // Keep focus stable. Only move focus when caller provides a targetId
            // (e.g., user clicked a variant option). Avoid focusing on initial load
            // to prevent mobile scroll-jumps.
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      // Simplified: prefer reading JSON from a script[type="application/json"][data-selected-variant]
      getSelectedVariant(productInfoNode) {
        // Prefer a script[type="application/json"][data-selected-variant] inside variant-selects
        const script = productInfoNode.querySelector(
          'variant-selects script[type="application/json"][data-selected-variant]',
        );

        // Helper: normalize parsed object keys (trim) and fix title casing if needed
        const normalizeResult = (obj) => {
          if (!obj) return null;
          const out = {};
          Object.keys(obj).forEach((k) => {
            out[k.trim()] = obj[k];
          });
          if (
            typeof out.title === "string" &&
            out.title === out.title.toLowerCase()
          ) {
            out.title = out.title.charAt(0).toUpperCase() + out.title.slice(1);
          }
          return out;
        };

        // If script exists, use its textContent (safe JSON embedding)
        if (script && script.textContent && script.textContent.trim()) {
          try {
            return normalizeResult(JSON.parse(script.textContent.trim()));
          } catch (e) {
            console.warn(
              "product-info: failed to parse selected-variant JSON from script",
              e,
            );
            return null;
          }
        }

        // Fallback: find any element with data-selected-variant and try to parse its textContent/attribute
        const node = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        );
        if (!node) return null;
        const payload = (
          node.textContent ||
          node.getAttribute("data-selected-variant") ||
          ""
        ).trim();
        if (!payload) return null;
        try {
          return normalizeResult(JSON.parse(payload));
        } catch (e) {
          // Fallback: try to extract a JSON-like {...} substring from the
          // surrounding HTML (handles test fixtures where attributes were
          // split by the HTML parser).
          try {
            const outer =
              productInfoNode.innerHTML || productInfoNode.outerHTML || "";
            const braceMatch = outer.match(/\{[\s\S]*?\}/);
            if (braceMatch && braceMatch[0])
              return normalizeResult(JSON.parse(braceMatch[0]));
          } catch (e2) {
            /* ignore */
          }
          console.warn(
            "product-info: failed to parse selected-variant JSON",
            e,
          );
          return null;
        }
      }

      // Read all variants JSON embedded in the variant-selects block, falling
      // back to window.productVariants if present. Returns an array of
      // variant objects (may be empty).
      getAllVariants() {
        try {
          const vs = this.variantSelectors;
          if (vs) {
            const script = vs.querySelector(
              'script[type="application/json"][data-variants]',
            );
            if (script && script.textContent && script.textContent.trim()) {
              return JSON.parse(script.textContent.trim());
            }
          }
        } catch (e) {
          /* ignore parse errors */
        }

        try {
          // Older pages may still expose a global mapping; use it as a fallback
          const w =
            window.productVariants?.[parseInt(this.dataset.productId, 10)];
          if (Array.isArray(w)) return w;
        } catch (e) {
          /* ignore */
        }

        return [];
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);

        if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        // If variant selectors match a known variant, add variant param for convenience
        try {
          const vs = this.variantSelectors;
          if (vs) {
            const all = Array.from(vs.querySelectorAll("*[name]")).filter(
              (el) => el.getAttribute("name")?.startsWith("options["),
            );
            const vals = all.map((s) => s.value);
            const opts = {
              o1: vals[0] || null,
              o2: vals[1] || null,
              o3: vals[2] || null,
            };
            const pv = this.getAllVariants();
            const match = pv.find(
              (v) =>
                (!opts.o1 || v.option1 === opts.o1) &&
                (!opts.o2 || v.option2 === opts.o2) &&
                (!opts.o3 || v.option3 === opts.o3),
            );
            if (match?.id) params.push(`variant=${String(match.id)}`);
          }
        } catch (e) {
          /* best-effort: ignore selector parsing failures */
        }

        const qs = params.join("&");
        return `${url}${qs ? `?${qs}` : ""}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (!variantSelects || !this.variantSelectors) return;
        const currentSelectedIds =
          this.variantSelectors?.selectedOptionValues?.slice?.() || [];

        HTMLUpdateUtility.setInnerHTML(
          this.variantSelectors,
          variantSelects.innerHTML,
        );

        // If suppression is requested (initial server-chosen default), do
        // NOT re-apply the client's previous selections. Instead, ensure
        // the server-selected inputs have their metadata refreshed so
        // swatches / selected-value displays update correctly.
        if (this._suppressVariantSelectorRestore) {
          try {
            const serverSelected = Array.from(
              this.variantSelectors.querySelectorAll(
                "select option[selected], fieldset input:checked",
              ),
            );
            serverSelected.forEach((sel) =>
              this.variantSelectors.updateSelectionMetadata({ target: sel }),
            );
            // Ensure availability/disabled UI matches the server-selected
            // inputs immediately after the DOM replacement.
            this.variantSelectors?.recalculateAvailability?.();
          } catch (e) {
            /* best-effort UI refresh */
          } finally {
            // Only suppress once (initial hydration)
            this._suppressVariantSelectorRestore = false;
          }

          return;
        }

        // Default behavior: attempt to preserve the client's selection when
        // the variant-selects block is replaced (e.g., after an option change)
        currentSelectedIds.forEach((id) => {
          if (!id) return;
          const el = this.variantSelectors.querySelector(
            `[data-option-value-id="${id}"]`,
          );
          if (!el) return;
          const tag = el.tagName;
          if (tag === "OPTION") {
            const select = el.closest("select");
            if (select) {
              select.value = el.value;
              el.setAttribute("selected", "selected");
            }
          } else if (tag === "INPUT" && el.type === "radio") {
            const name = el.getAttribute("name");
            Array.from(
              this.variantSelectors.querySelectorAll('input[type="radio"]'),
            ).forEach((r) => {
              if (r.getAttribute("name") === name) r.checked = false;
            });
            el.checked = true;
          }

          try {
            this.variantSelectors.updateSelectionMetadata({ target: el });
          } catch (e) {
            /* best-effort UI refresh */
          }
        });
        // After restoring prior selection metadata, ensure availability is consistent
        this.variantSelectors?.recalculateAvailability?.();
      }

      showDiscountedPriceFromBodyTag() {
        let useDynamicDiscount = document.body.getAttribute(
          "data-dynamic-discount",
        );
        let discountCode = document.body.getAttribute("data-discount-size");
        const googleDiscountCode = new URLSearchParams(
          window.location.search,
        ).get("pv2");

        if (!(useDynamicDiscount && discountCode)) return;

        // if the cart meta data has this variant id, suppress
        let hiddenField = document.getElementById("cart_metadata")?.value;
        if (hiddenField) {
          let variant = this.getSelectedVariant(
            document.querySelector("product-info[id^='MainProduct']"),
          );
          if (variant && variant.id) {
            if (hiddenField.includes(variant.id.toString())) return;
          }
          variant = this.getSelectedVariant(
            document.querySelector("product-info"),
          );
          if (variant && variant.id) {
            if (hiddenField.includes(variant.id.toString())) return;
          }
        }

        // hid current discount sections
        let discountPriceSpans = document.body.querySelectorAll(
          ".discount_code_mapping_price",
        );
        if (discountPriceSpans !== null) {
          discountPriceSpans.forEach((item) => {
            item.setAttribute("style", "display:none !important");
          });
        }

        var discountSet = false;
        let discountPriceSpan = document.body.querySelector(
          ".discount_code_mapping_price[data-discount='" + discountCode + "']",
        );
        if (discountPriceSpan !== null && !googleDiscountCode) {
          // is there a price for this code?
          discountPriceSpan.setAttribute("style", "display:initial !important");
          document.body
            .querySelector(".price__regular")
            .setAttribute("style", "display:none !important");
          document.body
            .querySelector(".price__sale")
            .setAttribute("style", "display:none !important");
          discountSet = true;
        }

        if (discountSet) {
          document
            .querySelector(".meta-discount-price-message")
            ?.classList.remove("visually-hidden");
        } else {
          document
            .querySelector(".meta-discount-price-message")
            ?.classList.add("visually-hidden");
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination(
            "Low-Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateLeadTime(
            variant.available,
            window.variantMetafields[variant.id].lead_time,
            window.variantMetafields[variant.id].qohlocal,
            window.variantMetafields[variant.id].jitflag,
            window.variantMetafields[variant.id].vendor,
          );
          this.updateCustomField(
            "Discountable",
            window.variantMetafields[variant.id].discountable,
          );
          this.updateCustomField(
            "Extra-Ship",
            window.variantMetafields[variant.id].extraship,
          );
          this.updateCustomField(
            "promo_message",
            window.variantMetafields[variant.id].promo_message,
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("aria-disabled") ?? true,
            window.variantStrings.soldOut,
          );

          this.showDiscountedPriceFromBodyTag();
          this.updateCollapsibleTabs(variant.id);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateLeadTime(available, days, localQty, jitFlag, vendor) {
        const field = document.getElementById(
          `Lead-Time-${this.dataset.section}`,
        );
        const vendorNames =
          "HORSEWARE,BRADLEY CALDWELL,ERS,HORZE,IRH,INTREPID,JT INTERNATIONAL,JPC,MUSTANG,OUTBACK,PERRI'S,SADDLERY BRANDS,SHIRES,WEAVER,WEATHERBEETA,DEVON AIRE,GARMIN,BOLLMAN";

        if (!field) {
          return;
        }

        if (localQty && localQty > 0) {
          days = 1;
          jitFlag = "N";
        }

        if (!vendorNames.includes(vendor.toUpperCase()) && localQty < 1) {
          jitFlag = "DS";
        }

        if (!days || !available) {
          field.parentNode.classList.add("visually-hidden");
          return;
        }

        days = parseInt(days);

        if (days == 1 || localQty > 0) {
          days = `1 business day`;
        } else if (days == 2) {
          days = `1-2 business days`;
        } else if (days == 3) {
          days = `1-3 business days`;
        } else if (days == 4) {
          days = `2-4 business days`;
        } else if (days == 5) {
          days = `3-5 business days`;
        } else if (days == 6) {
          days = `4-6 business days`;
        } else if (days == 7) {
          days = `5-7 business days`;
        } else if (days >= 8 && days <= 14) {
          days = `1-2 weeks`;
        } else if (days >= 15 && days <= 21) {
          days = `2-3 weeks`;
        } else if (days >= 22 && days <= 18) {
          days = `3-4 weeks`;
        } else if (days >= 29) {
          days = `1-2 months`;
        }

        field.parentNode.classList.remove("visually-hidden");
        if (days != true) {
          field.textContent = "Usually Ships in " + days ?? "";
        }
      }

      updateCustomField(name, value) {
        const field = document.getElementById(
          name + `-${this.dataset.section}`,
        );

        if (!field) {
          return;
        }

        if (!value || value == "$0.00") {
          field.parentNode.classList.add("visually-hidden");
          return;
        }

        field.parentNode.classList.remove("visually-hidden");
        if (value != true) {
          if (this.containsHtml(value)) {
            field.style.display = "block";
            field.innerHTML = value ?? "";
          } else {
            field.textContent = value ?? "";
          }
        }
      }

      containsHtml(value) {
        if (typeof value !== "string") return false;

        // Fast-path: no "<" at all → definitely plain text
        if (!value.includes("<")) return false;

        // Regex: < followed by a tag name (letters, numbers, hyphens, underscores)
        //        then optional attributes, then > or />
        //        Also catches self-closing tags and comments.
        const htmlTagRE = /<\s*(?:[a-z0-9_-]+|!--)/i;

        return htmlTagRE.test(value);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // Preserve tracking / campaign params (e.g., utm_*, trk_*) from current URL
        const preserveParamPatterns = [/^utm_/, /^trk_/];

        // Build the target URL object (works for relative or absolute urls)
        let target;
        try {
          target = new URL(url, window.location.origin);
        } catch (e) {
          target = new URL(window.location.origin + url);
        }

        // Ensure variant param is set/removed on the target URL
        if (variantId) {
          target.searchParams.set("variant", String(variantId));
        } else {
          target.searchParams.delete("variant");
        }

        // Merge preserved params from current location without overwriting existing ones
        try {
          const currentParams = new URLSearchParams(window.location.search);
          for (const [k, v] of currentParams.entries()) {
            if (
              preserveParamPatterns.some((re) => re.test(k)) &&
              !target.searchParams.has(k)
            ) {
              target.searchParams.set(k, v);
            }
          }
        } catch (e) {
          /* ignore parse errors */
        }

        const relativeUrl = `${target.pathname}${target.search}${target.hash}`;

        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${relativeUrl}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", relativeUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );

          let shouldRefresh = false;
          // For media items that exist in both source and destination, ensure
          // the source item's innerHTML is updated so dynamic pieces like
          // badges reflect the server-rendered variant-specific markup.
          mediaGalleryDestinationItems.forEach((destinationItem) => {
            const mid = destinationItem.dataset.mediaId;
            const sourceData = sourceMap.get(mid);
            if (
              sourceData &&
              sourceData.item &&
              sourceData.item.innerHTML !== destinationItem.innerHTML
            ) {
              sourceData.item.innerHTML = destinationItem.innerHTML;
              shouldRefresh = true;
            }
          });
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      /**
       * Updates any collapsible tabs that are visible and have a variant metafield
       * @param {*} variantId Selected variant id
       */
      updateCollapsibleTabs(variantId) {
        this.querySelectorAll('[data-variant-metafield="true"]').forEach(
          (tab) => {
            // get config data from tab's dataset
            const key = tab.dataset.variantMetafieldKey;
            const ns = tab.dataset.variantMetafieldNamespace ?? "custom";
            const value = window.variantMetafields[variantId]?.[ns]?.[key];
            const expected = tab.dataset.variantMetafieldValue;
            if (!key || !value) return;
            // booleans will automatically be strings on both sides
            if (value.toLowerCase() != expected.toLowerCase()) {
              tab.style.display = "none";
              tab.classList.add("hidden");
            } else {
              tab.style.display = "block";
              tab.classList.remove("hidden");
            }
          },
        );
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
